import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { useLaunchDarkly } from '@getaccept/lib-shared/src/launch-darkly/launch-darkly.composable';
import type { ProductProperty } from '@getaccept/lib-shared/src/product-library/product-properties/types/product-property';
import type {
  PricingTableColumn,
  PricingTableSection,
  PricingTableColumnType,
  PricingTableColumnUnitType,
} from '../types/pricing-table';
import { PricingTableHelper } from '../helpers/pricing-table.helper';
import type { Context } from '../types/enums';
import { NumberInputType, EPricingTableColumn } from '../types/enums';
import {
  maxCustomColumns,
  minColumnWidth,
  nonGridColumns,
  productPropertyReferenceContexts,
} from '../constants/pricing-table';

export function usePricingTableColumns(
  section: Ref<PricingTableSection>,
  preCalculated: Ref<boolean>,
  productProperties: Ref<ProductProperty[]> = ref([]),
  context: Ref<Context>,
  isResource: Ref<boolean> = ref(false)
) {
  const { flags } = useLaunchDarkly();

  const columns = computed(() => section.value?.columns || []);

  const enabledColumns = computed(() => PricingTableHelper.getEnabledColumns(columns.value));
  const columnWidths = computed(() => enabledColumns.value?.map(({ width }) => width));

  const customColumns = computed(() => columns.value.filter(({ isCustom }) => isCustom));
  const customColumnThresholdReached = computed(() => {
    const maxColumns = flags.value.pricingTableMultipleColumns ? maxCustomColumns : 1;
    return customColumns.value.length >= maxColumns;
  });

  const maxEnabledColumnsReached = computed(
    () => enabledColumns.value.length >= Math.floor(100 / minColumnWidth)
  );

  const isProductPropertyReferenceContext = computed(
    () => isResource.value || productPropertyReferenceContexts.includes(context.value)
  );

  const getColumnById = (columnId: string) => columns.value.find(({ id }) => id === columnId);

  const getColumnByName = (columnName: EPricingTableColumn) =>
    columns.value.find(({ name }) => name === columnName);

  const isColumnEnabled = (column: PricingTableColumn) => column?.enabled || false;
  const isGridColumn = (column: PricingTableColumn) =>
    !nonGridColumns.includes(column.name as EPricingTableColumn) || !!column.productPropertyId;

  const enableColumn = (column: PricingTableColumn, updatedSection?: PricingTableSection) =>
    preCalculated.value
      ? PricingTableHelper.enableColumn(column.id, updatedSection || section.value)
      : PricingTableHelper.enableColumnAndReCalculate(column.id, updatedSection || section.value);

  const disableColumn = (column: PricingTableColumn) => {
    let updatedSection = preCalculated.value
      ? PricingTableHelper.disableColumn(column.id, section.value)
      : PricingTableHelper.disableColumnAndReCalculate(column.id, section.value);

    if (column.name === EPricingTableColumn.TotalPrice) {
      const discountColumn = getColumnByName(EPricingTableColumn.Discount);
      updatedSection = preCalculated.value
        ? PricingTableHelper.disableColumn(discountColumn.id, updatedSection)
        : PricingTableHelper.disableColumnAndReCalculate(discountColumn.id, updatedSection);
    }

    return updatedSection;
  };

  const discountColumn = computed(() => getColumnByName(EPricingTableColumn.Discount));
  const totalPriceColumn = computed(() => getColumnByName(EPricingTableColumn.TotalPrice));

  const getColumnByProductPropertyId = (productPropertyId: string) =>
    columns.value.find(column => column.productPropertyId === productPropertyId);

  const getProductPropertyById = (productPropertyId: string) =>
    (productProperties.value || []).find(({ id }) => id === productPropertyId);

  const isProductPropertyEnabled = (productPropertyId: string) =>
    isColumnEnabled(getColumnByProductPropertyId(productPropertyId));

  const isCustomColumn = (column: PricingTableColumn) => column.isCustom;
  const isProductPropertyColumn = (column: PricingTableColumn) => column.productPropertyId;

  const createColumnFromProductProperty = (
    productPropertyId: string,
    columnProps?: Partial<PricingTableColumn>
  ) =>
    isProductPropertyReferenceContext.value
      ? PricingTableHelper.createColumn({ productPropertyId, ...(columnProps ?? {}) })
      : PricingTableHelper.createColumnFromProductProperty(
          getProductPropertyById(productPropertyId),
          columnProps
        );

  const insertColumn = (newColumn: PricingTableColumn): PricingTableSection => {
    const updatedColumns: PricingTableColumn[] = [
      ...section.value.columns.slice(0, -1),
      newColumn,
      section.value.columns[section.value.columns.length - 1],
    ];

    const updatedRows = section.value.rows.map(row => ({
      ...row,
      values: [...row.values, { columnId: newColumn.id, value: '' }],
    }));

    return {
      ...section.value,
      rows: updatedRows,
      columns: updatedColumns,
    };
  };

  const enableProductProperty = (productPropertyId: string) => {
    const existingColumn = getColumnByProductPropertyId(productPropertyId);
    if (existingColumn) {
      return enableColumn(existingColumn);
    }

    const column = createColumnFromProductProperty(productPropertyId);
    const updatedSection = insertColumn(column);

    return enableColumn(column, updatedSection);
  };

  const disableProductProperty = (productPropertyId: string) =>
    disableColumn(getColumnByProductPropertyId(productPropertyId));

  const toggleProductProperty = (productPropertyId: string) => {
    const toggleFn = isProductPropertyEnabled(productPropertyId)
      ? disableProductProperty
      : enableProductProperty;

    return toggleFn(productPropertyId);
  };

  const getColumnDisplayName = (column: PricingTableColumn) => {
    if (column.displayName) {
      return column.displayName;
    }

    const translationKey =
      column.name === EPricingTableColumn.TotalPrice ? 'total-price' : column.name;

    return translationKey ? t(translationKey) : '';
  };

  const getColumnType = (column: PricingTableColumn): PricingTableColumnType => {
    if (column.columnType) {
      return column.columnType;
    }

    if (column.isCustom) {
      return 'Text';
    }

    switch (column.name) {
      case EPricingTableColumn.Name:
      case EPricingTableColumn.Description:
      case EPricingTableColumn.Sku:
        return 'Text';
      case EPricingTableColumn.Price:
      case EPricingTableColumn.Units:
      case EPricingTableColumn.Tax:
      case EPricingTableColumn.TotalPrice:
      case EPricingTableColumn.NetPrice:
      case EPricingTableColumn.Discount:
        return 'Number';
    }

    return 'Text';
  };

  const getColumnAttribute = (column: PricingTableColumn, attr: keyof PricingTableColumn) => {
    const isSharedAttribute = ['displayName', 'name', 'columnType', 'unit'].includes(attr);
    const shouldReferenceProductProperty =
      isProductPropertyColumn(column) &&
      isProductPropertyReferenceContext.value &&
      isSharedAttribute;
    if (shouldReferenceProductProperty) {
      const productProperty = getProductPropertyById(column.productPropertyId);

      if (!productProperty) {
        return column[attr];
      }

      if (attr === 'columnType') {
        return productProperty.type;
      }

      return attr === 'displayName'
        ? column.displayName || productProperty.displayName
        : productProperty[attr];
    }

    switch (attr) {
      case 'displayName':
        return getColumnDisplayName(column);
      case 'columnType':
        return getColumnType(column);
      default:
        return column[attr];
    }
  };

  const getColumnNumberInputType = (columnId: string): NumberInputType => {
    const column = getColumnById(columnId);

    if (!column) {
      return NumberInputType.Number;
    }

    const unit: PricingTableColumnUnitType = getColumnAttribute(column, 'unit');

    if (unit) {
      switch (unit) {
        case 'Currency':
          return NumberInputType.Currency;
        case 'Percentage':
          return NumberInputType.Percentage;
        case 'Unknown':
          return NumberInputType.Number;
      }
    }

    switch (column?.name) {
      case EPricingTableColumn.Price:
      case EPricingTableColumn.TotalPrice:
        return NumberInputType.Currency;
      case EPricingTableColumn.Tax:
        return NumberInputType.Percentage;
      default:
        return NumberInputType.Number;
    }
  };

  return {
    columns,
    enabledColumns,
    columnWidths,
    customColumns,
    discountColumn,
    totalPriceColumn,
    customColumnThresholdReached,
    maxEnabledColumnsReached,
    getColumnAttribute,
    getColumnByName,
    getColumnById,
    isColumnEnabled,
    isGridColumn,
    enableColumn,
    disableColumn,
    isProductPropertyEnabled,
    enableProductProperty,
    disableProductProperty,
    toggleProductProperty,
    getColumnByProductPropertyId,
    createColumnFromProductProperty,
    isProductPropertyColumn,
    isCustomColumn,
    getProductPropertyById,
    getColumnNumberInputType,
  };
}
